import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../helpers/auth.services';

@Injectable({ providedIn: 'root' })
export class ChatbotService {

  private chatbotBase = environment.chatbotBase;

  private url         = `${this.chatbotBase}/chat`;           // ← Changed
  private modulesUrl  = `${this.chatbotBase}/modules`;        // ← Changed
  private feedbackUrl = `${this.chatbotBase}/feedback`;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService
  ) {}

  private getUser(): any {
    // Primary: use AuthenticationService's BehaviorSubject (always up to date)
    const u = this.authService.currentUserValue;
    if (u) return u;

    // Fallback: read directly from sessionStorage (same key auth service uses)
    try {
      const raw = sessionStorage.getItem('currentUser');
      if (raw) return JSON.parse(raw);
    } catch {}

    return null;
  }

  get isLoggedIn(): boolean {
    return !!this.getUser();
  }

  /**
   * Send a question to the chatbot backend.
   * @param module   Optional module name the user picked from the selector.
   *                 Backend treats this as a HINT, not a hard restriction —
   *                 role-based access is still enforced server-side.
   * @param branchId Active branch scope (mandatory filter server-side).
   * @param language Reply language: 'en' | 'hi' | 'mr'. Only changes the
   *                 wording of the answer -- SQL generation and data
   *                 accuracy are completely unaffected by this.
   */
  ask(
    question: string,
    siteId: number,
    workOrderId: number,
    history: { role: string, content: string }[] = [],
    module: string | null = null,
    branchId: number | null = null,   // NEW
    language: string = 'en'           // NEW
  ): Observable<any> {
    const user = this.getUser();

    const payload = {
      question:             question,
      site_id:              siteId      || null,
      work_order_id:        workOrderId || null,
      company_key:          user?.companyKey  ?? null,
      user_id:              user?.id          ?? null,
      user_role:            Array.isArray(user?.roles)
                              ? user.roles[0]
                              : (user?.role ?? null),
      conversation_history: history,
      module:               module || null,
      branch_id:            branchId ?? null,   // NEW
      language:             language || 'en',   // NEW
    };

    console.log('🤖 Chatbot payload:', payload);
    return this.http.post<any>(this.url, payload);
  }

  /**
   * Fetch the live module list from the backend (driven by the `modules`
   * DB table — NOT hardcoded, since there are 900+ modules). Now also
   * sends user_role so the backend returns only the modules this role is
   * actually allowed to access (see GET /modules in chat_router.py) —
   * previously this returned every company module to every role.
   */
  getModules(): Observable<any> {
    const user = this.getUser();
    const companyKey = user?.companyKey ?? null;
    const userRole = Array.isArray(user?.roles) ? user.roles[0] : (user?.role ?? null);
    const qs: string[] = [];
    if (companyKey != null) qs.push(`company_key=${companyKey}`);
    if (userRole) qs.push(`user_role=${encodeURIComponent(userRole)}`);
    const params = qs.length ? `?${qs.join('&')}` : '';
    return this.http.get<any>(`${this.modulesUrl}${params}`);
  }

  /** Records a 👍/👎 on a previous answer, keyed by the log_id the
   *  backend returned on that /chat response. One-shot POST — no retry
   *  loop, no polling, so it can't add ongoing load. */
  feedback(
    logId: string,
    feedback: 'like' | 'dislike',
    question?: string,
    answer?: string
  ): Observable<any> {
    const user = this.getUser();
    const payload = {
      log_id:   logId,
      feedback: feedback,
      question: question || null,
      answer:   answer   || null,
      user_id:  user?.id ?? null,
    };
    return this.http.post<any>(this.feedbackUrl, payload);
  }

  /** Stable per-user key. Used by app.component to detect a different
   *  user logging in without a full page reload, so it doesn't restore
   *  the previous user's cached chat state (and stale greeting name)
   *  into the new user's chat panel. */
  get currentUserKey(): string | null {
    const u = this.getUser();
    return u?.id != null ? String(u.id) : null;
  }
}